import { useEffect, useState } from "react";
import {
  BusFront,
  CalendarDays,
  CheckCircle2,
  Gauge,
  Route as RouteIcon,
  UserRoundCog,
} from "lucide-react";
import { toast } from "sonner";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import Pagination from "ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { InputFieldset } from "../../ui/inputFieldset";
import { SelectFieldset } from "../../ui/selectFieldset";
import { SelectTriggerUI } from "../../ui/selectTrigger";
import { Requirements } from "./components/Requirements";
import { AvailableVehicle } from "./components/AvailableVehicle";
import Schedule from "./components/Schedule";

type FleetSource = "internal" | "regular-vendor" | "external-vendor";
type FleetStatus = "available" | "assigned" | "in-route" | "maintenance";
type AssignmentStatus = "scheduled" | "in-route" | "completed";
type ManagementMode = "vendor" | "driver" | null;
type MovementSortKey =
  | "movement"
  | "tour"
  | "pickup"
  | "pickupDate"
  | "pickupTime"
  | "dropoff"
  | "dropoffDate"
  | "dropoffTime"
  | "pax"
  | "status";

type TransportTab = "requirements" | "fleet" | "driver-schedule" | "schedule";

type SortDirection = "asc" | "desc";

type Vehicle = {
  id: string;
  registration: string;
  type: string;
  capacity: number;
  source: FleetSource;
  vendor: string;
  location: string;
  status: FleetStatus;
  driverId?: string;
  nextMovement?: string;
};

type Driver = {
  id: string;
  name: string;
  employeeNo: string;
  maxHours: number;
  usedHours: number;
  maxShifts: number;
  usedShifts: number;
  license: string;
  status: "available" | "on-duty" | "rest";
};

type TransportRoute = {
  id: string;
  name: string;
  from: string;
  to: string;
  distance: number;
  duration: number;
  checkpoints: number;
  permit: string;
};

type Vendor = {
  id: string;
  name: string;
  category: "Regular" | "External";
  vehicles: number;
  vehicleTypes: string;
  area: string;
  contact: string;
  status: "Active" | "Inactive";
};

type Assignment = {
  id: string;
  vehicleId: string;
  driverId?: string;
  routeId: string;
  movement: string;
  group: string;
  pax: number;
  start: string;
  end: string;
  hours: number;
  status: AssignmentStatus;
  movementId?: string;
  price?: number;
};

type TourMovement = {
  id: string;
  tour: string;
  operator: string;
  tourType: string;
  date: string;
  pax: number;
  movement:
    | "Arrival Transfer"
    | "Inter-venue Transfer"
    | "Atraaf Travel"
    | "Departure Transfer";
  routeId: string;
  start: string;
  source: "Tour" | "Atraaf Master";
  lawazimPrice: number;
};

const INITIAL_VEHICLES: Vehicle[] = [
  {
    id: "V-101",
    registration: "KRB-2048",
    type: "Coach",
    capacity: 48,
    source: "internal",
    vendor: "FHIMS Vehicles",
    location: "Najaf Depot",
    status: "assigned",
    driverId: "D-01",
    nextMovement: "08:45 · Najaf Airport",
  },
  {
    id: "V-102",
    registration: "NJF-1187",
    type: "Bus",
    capacity: 30,
    source: "internal",
    vendor: "FHIMS Vehicles",
    location: "Najaf Airport",
    status: "available",
  },
  {
    id: "V-103",
    registration: "BGD-7782",
    type: "Van",
    capacity: 12,
    source: "internal",
    vendor: "FHIMS Vehicles",
    location: "Najaf",
    status: "assigned",
    driverId: "D-03",
    nextMovement: "15:00 · Najaf Atraaf",
  },
  {
    id: "V-104",
    registration: "KRB-6621",
    type: "Mini Bus",
    capacity: 20,
    source: "internal",
    vendor: "FHIMS Vehicles",
    location: "Karbala Depot",
    status: "maintenance",
  },
  {
    id: "V-301",
    registration: "EV-7184",
    type: "Van",
    capacity: 14,
    source: "external-vendor",
    vendor: "Safar External Vehicles",
    location: "Najaf",
    status: "available",
  },
  {
    id: "V-302",
    registration: "EV-6608",
    type: "Coach",
    capacity: 46,
    source: "external-vendor",
    vendor: "Iraq Transit Partner",
    location: "Karbala",
    status: "available",
  },
  {
    id: "V-303",
    registration: "EV-8831",
    type: "Bus",
    capacity: 34,
    source: "external-vendor",
    vendor: "Iraq Transit Partner",
    location: "Baghdad",
    status: "assigned",
    nextMovement: "18:10 · Najaf Airport",
  },
];

const INITIAL_DRIVERS: Driver[] = [
  {
    id: "D-01",
    name: "Abbas Kareem",
    employeeNo: "EMP-1042",
    maxHours: 8,
    usedHours: 3,
    maxShifts: 2,
    usedShifts: 1,
    license: "Valid to 2028",
    status: "on-duty",
  },
  {
    id: "D-02",
    name: "Haider Salman",
    employeeNo: "EMP-1178",
    maxHours: 12,
    usedHours: 5,
    maxShifts: 2,
    usedShifts: 1,
    license: "Valid to 2027",
    status: "available",
  },
  {
    id: "D-03",
    name: "Murtaza Ali",
    employeeNo: "EMP-1224",
    maxHours: 8,
    usedHours: 3,
    maxShifts: 2,
    usedShifts: 1,
    license: "Valid to 2029",
    status: "on-duty",
  },
  {
    id: "D-04",
    name: "Yusuf Najafi",
    employeeNo: "EMP-1305",
    maxHours: 8,
    usedHours: 8,
    maxShifts: 2,
    usedShifts: 2,
    license: "Valid to 2028",
    status: "rest",
  },
];

const INITIAL_ROUTES: TransportRoute[] = [
  {
    id: "R-01",
    name: "Najaf Airport → Najaf",
    from: "Najaf Airport",
    to: "Najaf",
    distance: 12,
    duration: 1,
    checkpoints: 1,
    permit: "Standard",
  },
  {
    id: "R-02",
    name: "Baghdad Airport → Karbala",
    from: "Baghdad Airport",
    to: "Karbala",
    distance: 115,
    duration: 2.5,
    checkpoints: 3,
    permit: "Intercity permit",
  },
  {
    id: "R-03",
    name: "Najaf → Karbala",
    from: "Najaf",
    to: "Karbala",
    distance: 80,
    duration: 1.5,
    checkpoints: 2,
    permit: "Intercity permit",
  },
  {
    id: "R-04",
    name: "Karbala → Samarra",
    from: "Karbala",
    to: "Samarra",
    distance: 185,
    duration: 3.5,
    checkpoints: 4,
    permit: "Long-route permit",
  },
  {
    id: "R-05",
    name: "Najaf Atraaf Circuit",
    from: "Najaf",
    to: "Najaf",
    distance: 46,
    duration: 3,
    checkpoints: 2,
    permit: "Atraaf route approval",
  },
  {
    id: "R-06",
    name: "Karbala Atraaf Circuit",
    from: "Karbala",
    to: "Karbala",
    distance: 58,
    duration: 4,
    checkpoints: 3,
    permit: "Atraaf route approval",
  },
  {
    id: "R-07",
    name: "Karbala → Najaf",
    from: "Karbala",
    to: "Najaf",
    distance: 80,
    duration: 1.5,
    checkpoints: 2,
    permit: "Intercity permit",
  },
  {
    id: "R-08",
    name: "Najaf → Najaf Airport",
    from: "Najaf",
    to: "Najaf Airport",
    distance: 12,
    duration: 1,
    checkpoints: 1,
    permit: "Standard",
  },
];

const INITIAL_VENDORS: Vendor[] = [
  {
    id: "VN-01",
    name: "Al Noor Transport",
    category: "Regular",
    vehicles: 12,
    vehicleTypes: "Coach, Bus, Van",
    area: "Najaf, Karbala",
    contact: "+964 770 111 2048",
    status: "Active",
  },
  {
    id: "VN-02",
    name: "Karbala Mobility Co.",
    category: "Regular",
    vehicles: 8,
    vehicleTypes: "Bus, Mini Bus",
    area: "Karbala",
    contact: "+964 781 220 6621",
    status: "Active",
  },
  {
    id: "VN-03",
    name: "Safar External Vehicles",
    category: "External",
    vehicles: 5,
    vehicleTypes: "Van, Coach",
    area: "Najaf",
    contact: "+964 750 883 7184",
    status: "Active",
  },
  {
    id: "VN-04",
    name: "Iraq Transit Partner",
    category: "External",
    vehicles: 9,
    vehicleTypes: "Coach, Bus",
    area: "Baghdad, Karbala",
    contact: "+964 790 660 8831",
    status: "Active",
  },
];

const INITIAL_ASSIGNMENTS: Assignment[] = [
  {
    id: "AS-01",
    vehicleId: "V-101",
    driverId: "D-01",
    routeId: "R-01",
    movement: "Arrival Transfer",
    group: "Ziyarat Najaf 2026",
    pax: 42,
    start: "08:45",
    end: "09:45",
    hours: 1,
    status: "scheduled",
    movementId: "TM-01",
    price: 840,
  },
  {
    id: "AS-02",
    vehicleId: "V-202",
    routeId: "R-02",
    movement: "Arrival Transfer",
    group: "Miqaat Karbala 2026",
    pax: 28,
    start: "11:20",
    end: "13:50",
    hours: 2.5,
    status: "in-route",
    movementId: "TM-05",
    price: 700,
  },
  {
    id: "AS-03",
    vehicleId: "V-303",
    routeId: "R-08",
    movement: "Departure Transfer",
    group: "External Hotel Test Tour",
    pax: 31,
    start: "18:10",
    end: "19:10",
    hours: 1,
    status: "scheduled",
    movementId: "TM-12",
    price: 620,
  },
  {
    id: "AS-04",
    vehicleId: "V-103",
    driverId: "D-03",
    routeId: "R-05",
    movement: "Atraaf Travel",
    group: "Instant Najaf Test Tour",
    pax: 12,
    start: "15:00",
    end: "18:00",
    hours: 3,
    status: "scheduled",
    movementId: "TM-10",
    price: 360,
  },
];

const TOUR_MOVEMENTS: TourMovement[] = [
  {
    id: "TM-01",
    tour: "Ziyarat Najaf 2026",
    operator: "Al Burhani Tours",
    tourType: "Faiz",
    date: "2026-08-12",
    pax: 42,
    movement: "Arrival Transfer",
    routeId: "R-01",
    start: "08:45",
    source: "Tour",
    lawazimPrice: 840,
  },
  {
    id: "TM-02",
    tour: "Ziyarat Najaf 2026",
    operator: "Al Burhani Tours",
    tourType: "Faiz",
    date: "2026-08-12",
    pax: 42,
    movement: "Atraaf Travel",
    routeId: "R-05",
    start: "14:30",
    source: "Atraaf Master",
    lawazimPrice: 1050,
  },
  {
    id: "TM-03",
    tour: "Ziyarat Najaf 2026",
    operator: "Al Burhani Tours",
    tourType: "Faiz",
    date: "2026-08-12",
    pax: 42,
    movement: "Inter-venue Transfer",
    routeId: "R-03",
    start: "19:00",
    source: "Tour",
    lawazimPrice: 630,
  },
  {
    id: "TM-04",
    tour: "Ziyarat Najaf 2026",
    operator: "Al Burhani Tours",
    tourType: "Faiz",
    date: "2026-08-12",
    pax: 42,
    movement: "Departure Transfer",
    routeId: "R-08",
    start: "22:00",
    source: "Tour",
    lawazimPrice: 840,
  },
  {
    id: "TM-05",
    tour: "Miqaat Karbala 2026",
    operator: "Saifee Tours & Travel",
    tourType: "Miqaat",
    date: "2026-08-12",
    pax: 28,
    movement: "Arrival Transfer",
    routeId: "R-02",
    start: "11:20",
    source: "Tour",
    lawazimPrice: 700,
  },
  {
    id: "TM-06",
    tour: "Miqaat Karbala 2026",
    operator: "Saifee Tours & Travel",
    tourType: "Miqaat",
    date: "2026-08-12",
    pax: 28,
    movement: "Atraaf Travel",
    routeId: "R-06",
    start: "16:00",
    source: "Atraaf Master",
    lawazimPrice: 980,
  },
  {
    id: "TM-07",
    tour: "Miqaat Karbala 2026",
    operator: "Saifee Tours & Travel",
    tourType: "Miqaat",
    date: "2026-08-12",
    pax: 28,
    movement: "Inter-venue Transfer",
    routeId: "R-07",
    start: "21:00",
    source: "Tour",
    lawazimPrice: 560,
  },
  {
    id: "TM-08",
    tour: "Instant Najaf Test Tour",
    operator: "Najaf Instant Tours",
    tourType: "Instant",
    date: "2026-08-12",
    pax: 12,
    movement: "Arrival Transfer",
    routeId: "R-01",
    start: "09:30",
    source: "Tour",
    lawazimPrice: 240,
  },
  {
    id: "TM-09",
    tour: "Instant Najaf Test Tour",
    operator: "Najaf Instant Tours",
    tourType: "Instant",
    date: "2026-08-12",
    pax: 12,
    movement: "Inter-venue Transfer",
    routeId: "R-03",
    start: "12:30",
    source: "Tour",
    lawazimPrice: 300,
  },
  {
    id: "TM-10",
    tour: "Instant Najaf Test Tour",
    operator: "Najaf Instant Tours",
    tourType: "Instant",
    date: "2026-08-12",
    pax: 12,
    movement: "Atraaf Travel",
    routeId: "R-05",
    start: "15:00",
    source: "Atraaf Master",
    lawazimPrice: 360,
  },
  {
    id: "TM-11",
    tour: "External Hotel Test Tour",
    operator: "External Hotel Operator",
    tourType: "External",
    date: "2026-08-12",
    pax: 31,
    movement: "Arrival Transfer",
    routeId: "R-02",
    start: "10:10",
    source: "Tour",
    lawazimPrice: 620,
  },
  {
    id: "TM-12",
    tour: "External Hotel Test Tour",
    operator: "External Hotel Operator",
    tourType: "External",
    date: "2026-08-12",
    pax: 31,
    movement: "Departure Transfer",
    routeId: "R-08",
    start: "18:10",
    source: "Tour",
    lawazimPrice: 620,
  },
  {
    id: "TM-13",
    tour: "Burhani Family Ziyarat",
    operator: "Burhani Holidays",
    tourType: "Family",
    date: "2026-08-12",
    pax: 18,
    movement: "Arrival Transfer",
    routeId: "R-01",
    start: "07:30",
    source: "Tour",
    lawazimPrice: 420,
  },
  {
    id: "TM-14",
    tour: "Karbala Heritage Group",
    operator: "Al Murtaza Travel",
    tourType: "Ziyarat",
    date: "2026-08-12",
    pax: 36,
    movement: "Inter-venue Transfer",
    routeId: "R-07",
    start: "13:15",
    source: "Tour",
    lawazimPrice: 720,
  },
  {
    id: "TM-15",
    tour: "Najaf Atraaf Delegation",
    operator: "Faiz Travel Services",
    tourType: "Delegation",
    date: "2026-08-12",
    pax: 24,
    movement: "Atraaf Travel",
    routeId: "R-05",
    start: "10:45",
    source: "Atraaf Master",
    lawazimPrice: 600,
  },
  {
    id: "TM-16",
    tour: "Baghdad Arrival Group",
    operator: "Noor Al Safar",
    tourType: "Arrival",
    date: "2026-08-12",
    pax: 44,
    movement: "Arrival Transfer",
    routeId: "R-02",
    start: "17:20",
    source: "Tour",
    lawazimPrice: 880,
  },
  {
    id: "TM-17",
    tour: "Samarra Day Ziyarat",
    operator: "Karbala Tours Company",
    tourType: "Excursion",
    date: "2026-08-12",
    pax: 32,
    movement: "Inter-venue Transfer",
    routeId: "R-04",
    start: "06:15",
    source: "Tour",
    lawazimPrice: 960,
  },
];

const addHoursToTime = (start: string, hours: number) => {
  const [hour, minute] = start.split(":").map(Number);
  const total = (hour * 60 + minute + Math.round(hours * 60)) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const sourceLabel: Record<FleetSource, string> = {
  internal: "Internal",
  "regular-vendor": "Vendor",
  "external-vendor": "External",
};

const PAGE_SIZE = 10;

export function TransportDashboardPage() {
  const { setPageHeader } = usePageHeader();
  const [vehicles, setVehicles] = useState(INITIAL_VEHICLES);
  const [drivers, setDrivers] = useState(INITIAL_DRIVERS);
  const [routes] = useState<TransportRoute[]>(() => {
    try {
      const stored = JSON.parse(
        localStorage.getItem("fhims-transport-routes") || "[]",
      ) as Array<{
        id: string;
        routeName: string;
        origin: string;
        destination: string;
        distanceKm: number;
        estimatedHours: number;
        stops?: string;
        active: boolean;
      }>;
      if (!stored.length) return INITIAL_ROUTES;
      const masterRoutes = stored
        .filter((route) => route.active)
        .map((route) => ({
          id: route.id,
          name: route.routeName,
          from: route.origin,
          to: route.destination,
          distance: route.distanceKm,
          duration: route.estimatedHours,
          checkpoints: route.stops
            ? route.stops.split(",").filter(Boolean).length
            : 0,
          permit: "Master route",
        }));
      const requiredDemoRoutes = INITIAL_ROUTES.filter(
        (route) =>
          !masterRoutes.some(
            (master) =>
              master.from.toLowerCase() === route.from.toLowerCase() &&
              master.to.toLowerCase() === route.to.toLowerCase(),
          ),
      );
      return [...masterRoutes, ...requiredDemoRoutes];
    } catch {
      return INITIAL_ROUTES;
    }
  });
  const [vendors, setVendors] = useState(INITIAL_VENDORS);
  const [assignments, setAssignments] = useState(INITIAL_ASSIGNMENTS);
  const [date] = useState("2026-08-12");
  const [selectedTour] = useState("all");
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [reallocationId, setReallocationId] = useState<string | null>(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [selectedRouteId, setSelectedRouteId] = useState("");
  const [movement, setMovement] = useState("Arrival Transfer");
  const [group, setGroup] = useState("");
  const [pax, setPax] = useState("");
  const [startTime, setStartTime] = useState("");
  const [selectedMovementId, setSelectedMovementId] = useState("");
  const [managementMode, setManagementMode] = useState<ManagementMode>(null);
  const [managementValues, setManagementValues] = useState<
    Record<string, string>
  >({});
  const [draggedVehicleId, setDraggedVehicleId] = useState("");
  const [, setDraggedDriverId] = useState("");
  const [dropAssignment, setDropAssignment] = useState<{
    vehicleId: string;
    tour: string;
  } | null>(null);
  const [dropScope, setDropScope] = useState<"all" | "specific">("all");
  const [dropMovementId, setDropMovementId] = useState("");
  const [dropDriverId, setDropDriverId] = useState("");
  const [dropVendorId, setDropVendorId] = useState("");
  const [driverFilter, setDriverFilter] = useState("all");
  const [driverPage, setDriverPage] = useState(1);
  const [driverPageSize, setDriverPageSize] = useState(10);

  const [selectedMovementIds, setSelectedMovementIds] = useState<string[]>([]);
  const [bulkVehicleId, setBulkVehicleId] = useState("");
  const [movementSearch] = useState("");
  const [movementSort] = useState<MovementSortKey>("pickupTime");
  const [movementSortDirection] = useState<SortDirection>("asc");
  const [activeTab, setActiveTab] = useState<TransportTab>("requirements");
  const [requestExternalVehicle, setRequestExternalVehicle] = useState(false);

  useEffect(() => {
    setPageHeader({
      title: "Transport Operations Dashboard",
      icon: <BusFront className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const tourNames = Array.from(
    new Set(
      TOUR_MOVEMENTS.filter((item) => item.date === date).map(
        (item) => item.tour,
      ),
    ),
  );

  const filteredTourMovements = TOUR_MOVEMENTS.filter(
    (item) =>
      item.date === date &&
      (selectedTour === "all" || item.tour === selectedTour),
  );

  const flatTransportMovements = filteredTourMovements
    .filter((item) => {
      const route = routes.find((routeItem) => routeItem.id === item.routeId);
      const query = movementSearch.trim().toLowerCase();
      return (
        !query ||
        [
          item.tour,
          item.operator,
          item.movement,
          route?.name,
          route?.from,
          route?.to,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query))
      );
    })
    .sort((left, right) => {
      const leftRoute = routes.find((item) => item.id === left.routeId);
      const rightRoute = routes.find((item) => item.id === right.routeId);
      const values: Record<
        MovementSortKey,
        [string | number, string | number]
      > = {
        movement: [left.movement, right.movement],
        tour: [left.tour, right.tour],
        pickup: [leftRoute?.from || "", rightRoute?.from || ""],
        pickupDate: [left.date, right.date],
        pickupTime: [left.start, right.start],
        dropoff: [leftRoute?.to || "", rightRoute?.to || ""],
        dropoffDate: [left.date, right.date],
        dropoffTime: [
          leftRoute ? addHoursToTime(left.start, leftRoute.duration) : "",
          rightRoute ? addHoursToTime(right.start, rightRoute.duration) : "",
        ],
        pax: [left.pax, right.pax],
        status: [
          assignments.some((item) => item.movementId === left.id)
            ? "scheduled"
            : "unscheduled",
          assignments.some((item) => item.movementId === right.id)
            ? "scheduled"
            : "unscheduled",
        ],
      };
      const [leftValue, rightValue] = values[movementSort];
      const result =
        typeof leftValue === "number" && typeof rightValue === "number"
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue));
      return movementSortDirection === "asc" ? result : -result;
    });

  const filteredDrivers = drivers.filter((driver) => {
    const hasAssignment = assignments.some(
      (assignment) =>
        assignment.driverId === driver.id && assignment.status !== "completed",
    );
    if (driverFilter === "assigned") return hasAssignment;
    if (driverFilter === "available")
      return !hasAssignment && driver.status !== "rest";
    if (driverFilter === "unavailable") return driver.status === "rest";
    return true;
  });

  const paginatedDrivers = filteredDrivers.slice(
    (driverPage - 1) * driverPageSize,
    driverPage * driverPageSize,
  );

  const occupiedVehicles = vehicles.filter(
    (vehicle) => vehicle.status === "assigned" || vehicle.status === "in-route",
  ).length;

  const externalVehicleCount = vehicles.filter(
    (vehicle) =>
      vehicle.source === "external-vendor" && vehicle.status === "available",
  ).length;

  const availableVehicleCount = vehicles.filter(
    (vehicle) => vehicle.status === "available",
  ).length;

  const availableDrivers = drivers.filter((driver) => driver.status !== "rest");

  const selectedRoute = routes.find((route) => route.id === selectedRouteId);

  const selectedDriver = drivers.find(
    (driver) => driver.id === selectedDriverId,
  );

  const selectedVehicle = vehicles.find(
    (vehicle) => vehicle.id === selectedVehicleId,
  );

  const selectedVehicleSchedule = selectedVehicle
    ? assignments
        .filter(
          (assignment) =>
            assignment.vehicleId === selectedVehicle.id &&
            assignment.status !== "completed" &&
            assignment.id !== reallocationId,
        )
        .sort((left, right) => left.start.localeCompare(right.start))
    : [];

  const isBusVehicle = selectedVehicle?.type === "Bus";

  const timeToMinutes = (value: string) => {
    const [hours, minutes] = value.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const hasScheduleConflict = (
    resourceAssignments: Assignment[],
    resourceId: string,
    start: string,
    end: string,
    resourceKey: "vehicleId" | "driverId",
    excludeAssignmentId?: string | null,
  ) => {
    if (!start || !end) return false;

    const proposedStart = timeToMinutes(start);
    const proposedEnd = timeToMinutes(end);

    return resourceAssignments.some((item) => {
      if (item.id === excludeAssignmentId) return false;
      if (item[resourceKey] !== resourceId) return false;
      if (item.status === "completed") return false;

      return (
        proposedStart < timeToMinutes(item.end) &&
        proposedEnd > timeToMinutes(item.start)
      );
    });
  };

  const proposedEnd = selectedRoute
    ? addHoursToTime(startTime, selectedRoute.duration)
    : "";

  const vehicleScheduleConflict =
    !!selectedVehicle &&
    !isBusVehicle &&
    hasScheduleConflict(
      assignments,
      selectedVehicle.id,
      startTime,
      proposedEnd,
      "vehicleId",
      reallocationId,
    );

  const vehicleStatusUnavailable =
    !!selectedVehicle &&
    !isBusVehicle &&
    (selectedVehicle.status === "maintenance" ||
      selectedVehicle.status === "in-route");

  const vehicleUnavailable =
    vehicleScheduleConflict || vehicleStatusUnavailable;

  const driverScheduleConflict =
    !!selectedDriver &&
    hasScheduleConflict(
      assignments,
      selectedDriver.id,
      startTime,
      proposedEnd,
      "driverId",
      reallocationId,
    );

  const driverStatusUnavailable =
    !!selectedDriver && selectedDriver.status === "rest";

  const driverUnavailable = driverScheduleConflict || driverStatusUnavailable;

  const getVehicleOccupancy = (
    vehicleId: string,
    excludeAssignmentId?: string | null,
  ) =>
    assignments
      .filter(
        (assignment) =>
          assignment.vehicleId === vehicleId &&
          assignment.id !== excludeAssignmentId &&
          assignment.status !== "completed",
      )
      .reduce((total, assignment) => total + assignment.pax, 0);

  const reallocationAssignment = reallocationId
    ? assignments.find((item) => item.id === reallocationId)
    : undefined;

  const currentReallocationVehicle = reallocationAssignment
    ? vehicles.find((item) => item.id === reallocationAssignment.vehicleId)
    : undefined;

  const droppedVehicle = dropAssignment
    ? vehicles.find((vehicle) => vehicle.id === dropAssignment.vehicleId)
    : undefined;

  const droppedPendingMovements = dropAssignment
    ? TOUR_MOVEMENTS.filter(
        (item) =>
          item.date === date &&
          item.tour === dropAssignment.tour &&
          !assignments.some((assignment) => assignment.movementId === item.id),
      )
    : [];

  const droppedTargetMovements =
    dropScope === "all"
      ? droppedPendingMovements
      : droppedPendingMovements.filter((item) => item.id === dropMovementId);

  const clearAssignment = () => {
    setAssignmentOpen(false);
    setReallocationId(null);
    setSelectedVehicleId("");
    setSelectedDriverId("");
    setSelectedRouteId("");
    setMovement("Arrival Transfer");
    setGroup("");
    setPax("");
    setStartTime("");
    setSelectedMovementId("");
    setRequestExternalVehicle(false);
  };

  // const openAssignment = (vehicleId?: string) => {
  //   clearAssignment();
  //   setSelectedVehicleId(vehicleId || "");
  //   setSelectedDriverId(availableDrivers[0]?.id || "");
  //   setAssignmentOpen(true);
  // };

  const openMovementAssignment = (request: TourMovement) => {
    clearAssignment();
    setSelectedMovementId(request.id);
    setSelectedRouteId(request.routeId);
    setMovement(request.movement);
    setGroup(request.tour);
    setPax(String(request.pax));
    setStartTime(request.start);
    setSelectedDriverId(availableDrivers[0]?.id || "");
    setAssignmentOpen(true);
  };

  const openDriverSchedule = (driverId: string) => {
    clearAssignment();
    setSelectedDriverId(driverId);
    setAssignmentOpen(true);
  };

  const closeDroppedAssignment = () => {
    setDropAssignment(null);
    setDropScope("all");
    setDropMovementId("");
    setDropDriverId("");
    setDropVendorId("");
  };

  const confirmDroppedAssignment = () => {
    if (!dropAssignment) return;
    const vehicle = vehicles.find(
      (item) => item.id === dropAssignment.vehicleId,
    );
    const pending = TOUR_MOVEMENTS.filter(
      (item) =>
        item.date === date &&
        item.tour === dropAssignment.tour &&
        !assignments.some((assignment) => assignment.movementId === item.id),
    );
    const targets =
      dropScope === "all"
        ? pending
        : pending.filter((item) => item.id === dropMovementId);
    const driver = drivers.find((item) => item.id === dropDriverId);
    if (!vehicle || !targets.length)
      return toast.error("Select a valid pending transport event");
    if (vehicle.source === "internal" && !driver)
      return toast.error("Select an employee driver for this internal vehicle");
    if (vehicle.source === "external-vendor" && !dropVendorId)
      return toast.error("Select the vendor supplying this external vehicle");
    const totalHours = targets.reduce(
      (sum, item) =>
        sum +
        (routes.find((route) => route.id === item.routeId)?.duration ?? 0),
      0,
    );
    const created: Assignment[] = targets.map((request) => {
      const route = routes.find((item) => item.id === request.routeId);
      const routeHours = route?.duration ?? 0;
      return {
        id: `AS-${request.id}-${Date.now()}`,
        vehicleId: vehicle.id,
        driverId: vehicle.source === "internal" ? driver?.id : undefined,
        routeId: request.routeId,
        movement: request.movement,
        group: request.tour,
        pax: request.pax,
        start: request.start,
        end: addHoursToTime(request.start, routeHours),
        hours: routeHours,
        status: "scheduled",
        movementId: request.id,
        price: request.lawazimPrice,
      };
    });
    setAssignments((current) => [...current, ...created]);
    const dropVendor = vendors.find((item) => item.id === dropVendorId);
    setVehicles((current) =>
      current.map((item) =>
        item.id === vehicle.id
          ? {
              ...item,
              vendor:
                vehicle.source === "external-vendor"
                  ? dropVendor?.name || item.vendor
                  : item.vendor,
              status: "assigned",
              driverId: driver?.id,
              nextMovement: `${targets[0].start} · ${routes.find((route) => route.id === targets[0].routeId)?.from}`,
            }
          : item,
      ),
    );
    if (driver)
      setDrivers((current) =>
        current.map((item) =>
          item.id === driver.id
            ? {
                ...item,
                usedHours: item.usedHours + totalHours,
                usedShifts: item.usedShifts + 1,
                status: "on-duty",
              }
            : item,
        ),
      );
    toast.success(
      `${vehicle.registration} assigned to ${targets.length} transport event${targets.length > 1 ? "s" : ""}`,
    );
    closeDroppedAssignment();
  };

  // const openReallocation = (assignment: Assignment) => {
  //   setReallocationId(assignment.id);
  //   setSelectedVehicleId("");
  //   setSelectedDriverId(assignment.driverId || availableDrivers[0]?.id || "");
  //   setSelectedRouteId(assignment.routeId);
  //   setMovement(assignment.movement);
  //   setGroup(assignment.group);
  //   setPax(String(assignment.pax));
  //   setStartTime(assignment.start);
  //   setSelectedMovementId(assignment.movementId || "");
  //   setAssignmentOpen(true);
  // };

  const handleTourSelection = (tour: string) => {
    setGroup(tour);
    const request =
      TOUR_MOVEMENTS.find((item) => item.tour === tour && item.date === date) ||
      TOUR_MOVEMENTS.find((item) => item.tour === tour);
    if (!request) return;
    setSelectedMovementId(request.id);
    setSelectedRouteId(request.routeId);
    setMovement(request.movement);
    setPax(String(request.pax));
    setStartTime(request.start);
    setSelectedVehicleId("");
    setSelectedDriverId(availableDrivers[0]?.id || "");
  };

  const saveAssignment = () => {
    const route = routes.find((item) => item.id === selectedRouteId);

    const vehicle = vehicles.find((item) => item.id === selectedVehicleId);

    const existing = assignments.find((item) => item.id === reallocationId);

    if (!vehicle || !route || !group.trim() || !startTime) {
      return toast.error("Vehicle, route, tour and start time are required");
    }

    if (!selectedDriver) {
      return toast.error("Select a driver for this assignment");
    }

    if (
      !requestExternalVehicle &&
      getVehicleOccupancy(vehicle.id, reallocationId) + (Number(pax) || 0) >
        vehicle.capacity
    ) {
      return toast.error(
        `${vehicle.registration} does not have enough remaining capacity`,
      );
    }

    if (vehicleStatusUnavailable) {
      return toast.error(`${vehicle.registration} is currently unavailable`);
    }

    if (vehicleScheduleConflict) {
      return toast.error(
        `${vehicle.registration} already has another assignment during this time`,
      );
    }

    if (driverStatusUnavailable) {
      return toast.error(`${selectedDriver.name} is currently unavailable`);
    }

    if (driverScheduleConflict) {
      return toast.error(
        `${selectedDriver.name} already has another shift during this time`,
      );
    }

    const end = addHoursToTime(startTime, route.duration);

    const movementRequest = TOUR_MOVEMENTS.find(
      (item) => item.id === selectedMovementId,
    );
    const next: Assignment = {
      id: existing?.id || `AS-${Date.now()}`,
      vehicleId: vehicle.id,
      driverId: selectedDriverId,
      routeId: route.id,
      movement,
      group,
      pax: Number(pax) || movementRequest?.pax || 0,
      start: startTime,
      end,
      hours: route.duration,
      status: "scheduled",
      movementId: selectedMovementId || undefined,
      price: movementRequest?.lawazimPrice,
    };

    setAssignments((current) =>
      existing
        ? current.map((item) => (item.id === existing.id ? next : item))
        : [...current, next],
    );

    setVehicles((current) =>
      current.map((item) => {
        if (existing?.vehicleId === item.id && item.id !== vehicle.id) {
          return {
            ...item,
            status: "available",
            driverId: undefined,
            nextMovement: undefined,
          };
        }

        if (item.id === vehicle.id) {
          return {
            ...item,
            status: "assigned",
            driverId: selectedDriverId || undefined,
            vendor: item.vendor,
            nextMovement: `${startTime} · ${route.from}`,
          };
        }

        return item;
      }),
    );

    if (selectedDriver || existing?.driverId) {
      setDrivers((current) =>
        current.map((item) => {
          let usedHours = item.usedHours;
          let usedShifts = item.usedShifts;
          if (existing?.driverId === item.id) {
            usedHours = Math.max(0, usedHours - existing.hours);

            usedShifts = Math.max(0, usedShifts - 1);
          }
          if (selectedDriver?.id === item.id) {
            usedHours += route.duration;
            usedShifts += 1;
          }

          return {
            ...item,
            usedHours,
            usedShifts,
            status: usedHours > 0 ? "on-duty" : "available",
          };
        }),
      );
    }
    toast.success(
      existing
        ? "Vehicle reallocated and schedule updated"
        : "Vehicle and driver assigned successfully",
    );
    clearAssignment();
  };

  const managementConfig: Record<
    Exclude<ManagementMode, null>,
    { title: string; description: string; fields: string[] }
  > = {
    vendor: {
      title: "Add Vendor",
      description:
        "Register a regular or external transport vendor and its operating capability.",
      fields: [
        "Vendor Name",
        "Vendor Category",
        "Contact Number",
        "Vehicle Types",
        "Operating Areas",
        "Vehicle Count",
      ],
    },
    driver: {
      title: "Driver Availability",
      description:
        "Update whether an employee driver is available for assignment.",
      fields: ["Employee Driver", "Availability Status"],
    },
  };

  const saveManagement = () => {
    if (!managementMode) return;
    const required = managementConfig[managementMode].fields;
    const missing = required.find(
      (field) =>
        field !== "Permit (Optional)" && !managementValues[field]?.trim(),
    );
    if (missing) return toast.error(`${missing} is required`);
    if (managementMode === "vendor") {
      setVendors((current) => [
        ...current,
        {
          id: `VN-${Date.now()}`,
          name: managementValues["Vendor Name"],
          category: managementValues["Vendor Category"]
            .toLowerCase()
            .includes("external")
            ? "External"
            : "Regular",
          vehicles: Number(managementValues["Vehicle Count"]),
          vehicleTypes: managementValues["Vehicle Types"],
          area: managementValues["Operating Areas"],
          contact: managementValues["Contact Number"],
          status: "Active",
        },
      ]);
    } else if (managementMode === "driver") {
      setDrivers((current) =>
        current.map((item) =>
          item.name === managementValues["Employee Driver"]
            ? {
                ...item,
                status: managementValues["Availability Status"]
                  .toLowerCase()
                  .includes("unavailable")
                  ? "rest"
                  : "available",
              }
            : item,
        ),
      );
    }
    toast.success(`${managementConfig[managementMode].title} saved`);
    setManagementMode(null);
    setManagementValues({});
  };

  const assignSelectedTransfers = (targetVehicleId = bulkVehicleId) => {
    const vehicle = vehicles.find((item) => item.id === targetVehicleId);
    const selectedRequests = flatTransportMovements.filter((item) =>
      selectedMovementIds.includes(item.id),
    );
    if (!vehicle || !selectedRequests.length) {
      toast.error("Select one or more transfer lines and a vehicle");
      return;
    }
    if (vehicle.status === "maintenance") {
      toast.error("A vehicle under maintenance cannot be scheduled");
      return;
    }
    const newAssignments = selectedRequests
      .filter(
        (request) =>
          !assignments.some(
            (assignment) => assignment.movementId === request.id,
          ),
      )
      .map((request, index): Assignment => {
        const route = routes.find((item) => item.id === request.routeId);
        const hours = route?.duration ?? 0;
        return {
          id: `AS-BULK-${Date.now()}-${index}`,
          vehicleId: vehicle.id,
          driverId: vehicle.driverId,
          routeId: request.routeId,
          movement: request.movement,
          group: request.tour,
          pax: request.pax,
          start: request.start,
          end: addHoursToTime(request.start, hours),
          hours,
          status: "scheduled",
          movementId: request.id,
        };
      });
    if (!newAssignments.length) {
      toast.error("The selected transfer lines are already scheduled");
      return;
    }
    setAssignments((current) => [...current, ...newAssignments]);
    setVehicles((current) =>
      current.map((item) =>
        item.id === vehicle.id ? { ...item, status: "assigned" } : item,
      ),
    );
    setSelectedMovementIds([]);
    setBulkVehicleId("");
    toast.success(
      `${newAssignments.length} transfer line${newAssignments.length === 1 ? "" : "s"} assigned to ${vehicle.registration}`,
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50/60 to-blue-50 p-2">
      <div className="space-y-3">
        <Card
          className="border-0 bg-transparent shadow-none"
          style={{ marginTop: "0px", marginBottom: "10px" }}
        >
          <CardContent style={{ padding: "0px" }}>
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
              {[
                [
                  "Total Vehicles",
                  vehicles.length,
                  "bg-sky-500",
                  <BusFront className="h-4 w-4" />,
                ],
                [
                  "Occupied",
                  occupiedVehicles,
                  "bg-violet-500",
                  <RouteIcon className="h-4 w-4" />,
                ],
                [
                  "External Available",
                  externalVehicleCount,
                  "bg-amber-500",
                  <Gauge className="h-4 w-4" />,
                ],
                [
                  "Available",
                  availableVehicleCount,
                  "bg-emerald-500",
                  <CheckCircle2 className="h-4 w-4" />,
                ],
                [
                  "Drivers Available",
                  availableDrivers.length,
                  "bg-cyan-500",
                  <UserRoundCog className="h-4 w-4" />,
                ],
              ].map(([label, value, color, icon]) => (
                <div
                  key={String(label)}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
                >
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-white ${color}`}
                  >
                    {icon}
                  </span>
                  <div>
                    <div className="text-lg font-bold leading-none text-slate-900">
                      {value}
                    </div>
                    <div className="mt-1 text-[11px] font-semibold tracking-wide text-slate-500 lg:text-sm">
                      {label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="mb-3 flex flex-col gap-3 border-b border-border sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-0">
            {[
              {
                id: "requirements",
                label: "Requirements",
                icon: RouteIcon,
              },
              {
                id: "fleet",
                label: "Fleet",
                icon: BusFront,
              },
              {
                id: "driver-schedule",
                label: "Driver",
                icon: UserRoundCog,
              },
              {
                id: "schedule",
                label: "Schedule",
                icon: CalendarDays,
              },
            ].map(({ id, label, icon: Icon }, index) => {
              const isActive = activeTab === id;

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id as TransportTab)}
                  className={`
            relative flex items-center gap-2 px-5 py-3 text-sm
            ${isActive ? "text-primary" : "text-muted-foreground"}
            hover:text-foreground
          `}
                >
                  {/* <div
            className={`
              flex h-7 w-7 items-center justify-center rounded-full
              text-xs font-semibold
              ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }
            `}
          >
            {index + 1}
          </div> */}

                  <Icon className="h-4 w-4" />

                  <span className="font-medium">{label}</span>

                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-3">
          {activeTab === "requirements" && (
            <Requirements
              vehicles={vehicles}
              routes={routes}
              assignments={assignments}
              setAssignments={setAssignments}
              setVehicles={setVehicles}
              onAssignMovement={openMovementAssignment}
              draggedVehicleId={draggedVehicleId}
              setDraggedVehicleId={setDraggedVehicleId}
            />
          )}

          {activeTab === "fleet" && (
            <AvailableVehicle
              vehicles={vehicles}
              drivers={drivers}
              selectedMovementIds={selectedMovementIds}
              onAssignSelectedTransfers={assignSelectedTransfers}
              draggedVehicleId={draggedVehicleId}
              setDraggedVehicleId={setDraggedVehicleId}
            />
          )}

          {activeTab === "driver-schedule" && (
            <div className="order-4 grid items-stretch gap-3 xl:grid-cols-12">
              <Card
                className="h-full overflow-hidden rounded-xl border-slate-200 bg-white shadow-sm xl:col-span-12"
                style={{ gap: "0px" }}
              >
                <CardContent
                  className="flex h-full flex-col p-0"
                  style={{ padding: "0px" }}
                >
                  <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
                    <UserRoundCog className="h-5 w-5 shrink-0 text-sky-700" />

                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold leading-5 text-slate-900">
                        Employee Drivers
                      </div>

                      <div className="text-xs leading-5 text-slate-500">
                        Drivers can be assigned repeatedly whenever their
                        schedules are free
                      </div>
                    </div>

                    <div className="flex gap-2 shrink-0">
                      <Select
                        value={driverFilter}
                        onValueChange={(value) => {
                          setDriverFilter(value);
                          setDriverPage(1);
                        }}
                      >
                        <SelectTrigger className="h-9 w-full bg-white px-3 text-sm">
                          <SelectValue />
                        </SelectTrigger>

                        <SelectContent>
                          <SelectItem value="all">All Drivers</SelectItem>
                          <SelectItem value="assigned">Assigned</SelectItem>
                          <SelectItem value="available">Available</SelectItem>
                          <SelectItem value="unavailable">
                            Unavailable
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid flex-1 grid-cols-1 gap-2 bg-slate-50/50 p-3 md:grid-cols-2 xl:grid-cols-3">
                    {paginatedDrivers.map((driver) => {
                      const driverAssignments = assignments.filter(
                        (assignment) =>
                          assignment.driverId === driver.id &&
                          assignment.status !== "completed",
                      );
                      const assignedTours = Array.from(
                        new Set(
                          driverAssignments.map(
                            (assignment) => assignment.group,
                          ),
                        ),
                      );
                      const isAvailable =
                        driver.status !== "rest" && assignedTours.length === 0;
                      return (
                        <div
                          key={driver.id}
                          draggable={driver.status !== "rest"}
                          onDragStart={(event) => {
                            event.dataTransfer.effectAllowed = "copy";
                            setDraggedDriverId(driver.id);
                          }}
                          onDragEnd={() => setDraggedDriverId("")}
                          className={`rounded-lg border border-slate-200 bg-white px-3 py-2.5 transition ${driver.status !== "rest" ? "cursor-grab hover:border-emerald-200 hover:bg-emerald-50/40 active:cursor-grabbing" : "opacity-70"}`}
                        >
                          <div className="flex items-start gap-2">
                            <span
                              className={`mt-2 h-2 w-2 rounded-full ${driver.status === "rest" ? "bg-slate-400" : "bg-emerald-500"}`}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-semibold text-slate-900">
                                {driver.name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {driver.employeeNo} · {driver.license}
                              </div>
                            </div>
                            <span
                              className={`text-sm font-bold ${driver.status === "rest" ? "text-slate-500" : "text-emerald-700"}`}
                            >
                              {driver.status === "rest"
                                ? "Unavailable"
                                : assignedTours.length
                                  ? "Assigned"
                                  : "Available"}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center justify-between text-[7px] text-slate-500">
                            <span className="min-w-0 truncate text-xs text-muted-foreground">
                              {assignedTours.length
                                ? `Tour: ${assignedTours.join(", ")}`
                                : isAvailable
                                  ? "Free for assignment"
                                  : "Not available"}
                            </span>
                            {isAvailable ? (
                              <button
                                type="button"
                                className="text-sm font-semibold text-sky-700 hover:underline"
                                onClick={() => openDriverSchedule(driver.id)}
                              >
                                Assign vehicle
                              </button>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                Rest
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
                <div className="col-span-full rounded-lg bg-white">
                  <Pagination
                    totalItems={filteredDrivers.length}
                    itemsPerPage={PAGE_SIZE}
                    currentPage={driverPage}
                    onPageChange={setDriverPage}
                    onPageSizeChange={(size) => {
                      setDriverPageSize(size);
                      setDriverPage(1);
                    }}
                    label="drivers"
                  />
                </div>
              </Card>
            </div>
          )}

          {activeTab === "schedule" && <Schedule />}
        </div>

        <Dialog
          open={!!dropAssignment}
          onOpenChange={(open) => !open && closeDroppedAssignment()}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Assign Vehicle to Tour Transport</DialogTitle>
              <DialogDescription>
                Choose whether this vehicle covers every pending movement or one
                specific event.
              </DialogDescription>
            </DialogHeader>
            {dropAssignment && droppedVehicle && (
              <div className="space-y-3 py-2">
                <div className="grid gap-2 rounded-xl border border-sky-200 bg-sky-50 p-3 sm:grid-cols-2">
                  <div>
                    <div className="text-[9px] font-semibold uppercase tracking-wide text-sky-600">
                      Vehicle
                    </div>
                    <div className="mt-0.5 text-sm font-bold text-slate-900">
                      {droppedVehicle.registration}
                    </div>
                    <div className="text-[9px] text-slate-500">
                      {droppedVehicle.type} ·{" "}
                      {sourceLabel[droppedVehicle.source]}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] font-semibold uppercase tracking-wide text-sky-600">
                      Tour
                    </div>
                    <div className="mt-0.5 text-sm font-bold text-slate-900">
                      {dropAssignment.tour}
                    </div>
                    <div className="text-[9px] text-slate-500">
                      {droppedPendingMovements.length} pending transport event
                      {droppedPendingMovements.length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <div className="mb-1 text-xs font-medium text-slate-700">
                      Assignment Scope <span className="text-red-500">*</span>
                    </div>
                    <Select
                      value={dropScope}
                      onValueChange={(value) =>
                        setDropScope(value as "all" | "specific")
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All pending events</SelectItem>
                        <SelectItem value="specific">
                          One specific event
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {dropScope === "specific" && (
                    <div>
                      <div className="mb-1 text-xs font-medium text-slate-700">
                        Transport Event <span className="text-red-500">*</span>
                      </div>
                      <Select
                        value={dropMovementId}
                        onValueChange={setDropMovementId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select event" />
                        </SelectTrigger>
                        <SelectContent>
                          {droppedPendingMovements.map((item) => {
                            const route = routes.find(
                              (routeItem) => routeItem.id === item.routeId,
                            );
                            return (
                              <SelectItem key={item.id} value={item.id}>
                                {item.movement} · {item.start} · {route?.name}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {droppedVehicle.source === "internal" && (
                    <div
                      className={
                        dropScope === "all" ? "sm:col-span-1" : "sm:col-span-2"
                      }
                    >
                      <div className="mb-1 text-xs font-medium text-slate-700">
                        Employee Driver <span className="text-red-500">*</span>
                      </div>
                      <Select
                        value={dropDriverId}
                        onValueChange={setDropDriverId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a free driver" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableDrivers.map((item) => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name} · available if schedule is free
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {droppedVehicle.source === "external-vendor" && (
                    <div className="sm:col-span-2">
                      <div className="mb-1 text-xs font-medium text-slate-700">
                        Vendor sharing this vehicle{" "}
                        <span className="text-red-500">*</span>
                      </div>
                      <Select
                        value={dropVendorId}
                        onValueChange={setDropVendorId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select external vendor" />
                        </SelectTrigger>
                        <SelectContent>
                          {vendors
                            .filter(
                              (item) =>
                                item.status === "Active" &&
                                item.category === "External" &&
                                item.vehicleTypes.includes(droppedVehicle.type),
                            )
                            .map((item) => (
                              <SelectItem key={item.id} value={item.id}>
                                {item.name} · {item.area}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <div className="text-[10px] font-semibold text-amber-900">
                    Selected schedule
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[9px] text-amber-800">
                    <span>
                      {droppedTargetMovements.length} event
                      {droppedTargetMovements.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {droppedTargetMovements.map((item) => {
                      const route = routes.find(
                        (routeItem) => routeItem.id === item.routeId,
                      );
                      return (
                        <div
                          key={item.id}
                          className="flex items-center justify-between gap-2 rounded-md bg-white/70 px-2 py-1 text-[9px]"
                        >
                          <span className="font-medium text-slate-700">
                            {item.movement} · {route?.name}
                          </span>
                          <span className="shrink-0 text-slate-500">
                            {item.start}–
                            {route
                              ? addHoursToTime(item.start, route.duration)
                              : "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={closeDroppedAssignment}>
                Cancel
              </Button>
              <Button
                onClick={confirmDroppedAssignment}
                className="bg-sky-600 text-white hover:bg-sky-700"
              >
                Confirm Assignment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={assignmentOpen}
          onOpenChange={(open) => !open && clearAssignment()}
        >
          <DialogContent className="!w-[calc(100vw-1rem)] sm:!w-[60vw] !max-w-[1200px] !max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle>
                {reallocationId
                  ? "Reallocate Vehicle"
                  : "Create Vehicle Assignment"}
              </DialogTitle>
              <DialogDescription>
                {reallocationId
                  ? "Confirm the tour, choose an available replacement vehicle, then complete the normal assignment details."
                  : "Select a tour, vehicle, employee driver, route and pickup time."}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <SelectFieldset label="Tour" required>
                  <Select value={group} onValueChange={handleTourSelection}>
                    <SelectTriggerUI className="h-9 text-sm">
                      <SelectValue placeholder="Select tour first" />
                    </SelectTriggerUI>

                    <SelectContent>
                      {tourNames.map((item) => {
                        const details = TOUR_MOVEMENTS.find(
                          (movementItem) => movementItem.tour === item,
                        );

                        return (
                          <SelectItem key={item} value={item}>
                            {item} · {details?.operator} · {details?.tourType}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
              {reallocationId && currentReallocationVehicle && (
                <div className="sm:col-span-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                  <div className="text-[9px] font-semibold uppercase tracking-wide text-amber-700">
                    Current Assignment
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-700">
                    <span className="font-bold">
                      {currentReallocationVehicle.registration}
                    </span>
                    <span>
                      {currentReallocationVehicle.type} ·{" "}
                      {sourceLabel[currentReallocationVehicle.source]}
                    </span>
                    <span>{reallocationAssignment?.movement}</span>
                    <span>
                      Occupancy{" "}
                      {getVehicleOccupancy(
                        currentReallocationVehicle.id,
                        reallocationId,
                      )}
                      /{currentReallocationVehicle.capacity}
                    </span>
                  </div>
                </div>
              )}
              <label className="sm:col-span-2 flex cursor-pointer items-center gap-2 rounded-lg border border-sky-200 bg-sky-50/60 px-3 py-2 text-xs font-medium text-sky-800">
                <input
                  type="checkbox"
                  checked={requestExternalVehicle}
                  onChange={(event) => {
                    const checked = event.target.checked;

                    setRequestExternalVehicle(checked);

                    if (!checked) {
                      setSelectedVehicleId("");
                    }
                  }}
                  className="h-4 w-4 accent-sky-600"
                />
                Request external vehicle
              </label>
              {!requestExternalVehicle &&
                !isBusVehicle &&
                (vehicleUnavailable || driverUnavailable) && (
                  <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <div className="text-sm font-semibold text-amber-800">
                      Assignment availability warning
                    </div>

                    <div className="mt-1 space-y-1 text-xs text-amber-700">
                      {vehicleUnavailable && selectedVehicle && (
                        <div>
                          <span className="font-semibold">
                            Vehicle not available:
                          </span>{" "}
                          {selectedVehicle.registration}{" "}
                          {vehicleStatusUnavailable
                            ? `is currently ${selectedVehicle.status}.`
                            : "already has another assignment during this time."}
                        </div>
                      )}

                      {driverUnavailable && selectedDriver && (
                        <div>
                          <span className="font-semibold">
                            Employee driver not available:
                          </span>{" "}
                          {selectedDriver.name}{" "}
                          {driverStatusUnavailable
                            ? "is currently unavailable."
                            : "already has another assignment during this time."}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              {group && (
                <div className="sm:col-span-2">
                  <SelectFieldset label="Available Vehicle" required>
                    <Select
                      value={selectedVehicleId}
                      onValueChange={(value) => {
                        const vehicle = vehicles.find(
                          (item) => item.id === value,
                        );

                        setSelectedVehicleId(value);
                        setRequestExternalVehicle(
                          vehicle?.source === "external-vendor",
                        );
                      }}
                    >
                      <SelectTriggerUI className="h-9 text-sm">
                        <SelectValue placeholder="Select available vehicle" />
                      </SelectTriggerUI>

                      <SelectContent>
                        {vehicles
                          .filter(
                            (item) =>
                              item.status !== "maintenance" &&
                              item.status !== "in-route",
                          )
                          .map((item) => {
                            return (
                              <SelectItem key={item.id} value={item.id}>
                                {item.type} · {item.registration} ·{" "}
                                {item.source === "external-vendor"
                                  ? "External"
                                  : sourceLabel[item.source]}{" "}
                                · {item.location} · Occupancy{" "}
                                {getVehicleOccupancy(item.id, reallocationId)}/
                                {item.capacity}
                              </SelectItem>
                            );
                          })}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                </div>
              )}
              {selectedVehicle && (
                <div className="sm:col-span-2 overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-slate-50 px-3 py-2">
                    <div>
                      <div className="text-xs font-semibold text-slate-900">
                        Current vehicle schedule —{" "}
                        {selectedVehicle.registration}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        Review occupied routes before creating another
                        assignment.
                      </div>
                    </div>
                    <span
                      className={`rounded-full px-2 py-1 text-[10px] font-semibold ${selectedVehicleSchedule.length ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`}
                    >
                      {selectedVehicleSchedule.length
                        ? `${selectedVehicleSchedule.length} active route${selectedVehicleSchedule.length === 1 ? "" : "s"}`
                        : "Available"}
                    </span>
                  </div>
                  {selectedVehicleSchedule.length ? (
                    <div className="divide-y divide-slate-100">
                      {selectedVehicleSchedule.map((schedule) => {
                        const scheduleRoute = routes.find(
                          (route) => route.id === schedule.routeId,
                        );
                        return (
                          <div
                            key={schedule.id}
                            className="grid gap-2 px-3 py-2 text-xs sm:grid-cols-[1.1fr_1.5fr_110px_90px]"
                          >
                            <div>
                              <span className="block text-[9px] uppercase text-slate-400">
                                Tour
                              </span>
                              <b className="text-slate-800">{schedule.group}</b>
                              <div className="text-[10px] text-slate-500">
                                {schedule.movement}
                              </div>
                            </div>
                            <div>
                              <span className="block text-[9px] uppercase text-slate-400">
                                Assigned route
                              </span>
                              <b className="text-sky-700">
                                {scheduleRoute?.from || "—"} →{" "}
                                {scheduleRoute?.to || "—"}
                              </b>
                              <div className="text-[10px] text-slate-500">
                                {scheduleRoute?.name || schedule.routeId}
                              </div>
                            </div>
                            <div>
                              <span className="block text-[9px] uppercase text-slate-400">
                                Schedule
                              </span>
                              <b>
                                {schedule.start}–{schedule.end}
                              </b>
                            </div>
                            <div>
                              <span className="block text-[9px] uppercase text-slate-400">
                                Passengers
                              </span>
                              <b>{schedule.pax} pax</b>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="px-3 py-3 text-xs text-emerald-700">
                      No active route is assigned to this vehicle for the
                      current schedule.
                    </div>
                  )}
                </div>
              )}
              <div>
                <InputFieldset
                  label="Pick Up"
                  required
                  id="pickup"
                  value={selectedRoute?.from || ""}
                  readOnly
                  placeholder="Select pickup"
                  className="text-sm"
                />
              </div>

              <div>
                <InputFieldset
                  label="Drop"
                  required
                  id="drop"
                  value={selectedRoute?.to || ""}
                  readOnly
                  placeholder="Select drop"
                  className="text-sm"
                />
              </div>
              <div>
                <InputFieldset
                  label="Pickup Time"
                  required
                  id="pickupTime"
                  type="time"
                  value={startTime}
                  onChange={(event) => setStartTime(event.target.value)}
                  className="text-sm"
                />
              </div>

              <div>
                <SelectFieldset label="Employee Driver" required>
                  <Select
                    value={selectedDriverId}
                    onValueChange={setSelectedDriverId}
                  >
                    <SelectTriggerUI className="h-9 text-sm">
                      <SelectValue placeholder="Select a free driver" />
                    </SelectTriggerUI>

                    <SelectContent>
                      {availableDrivers.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name} · {item.employeeNo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>

              {selectedVehicle && !requestExternalVehicle && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 p-2">
                  <div className="text-sm font-semibold tracking-wide text-sky-700">
                    Vehicle occupancy
                  </div>
                  <div className="text-xs font-bold text-sky-900">
                    {getVehicleOccupancy(selectedVehicle.id, reallocationId)} /{" "}
                    {selectedVehicle.capacity} pax occupied
                  </div>
                  <div className="text-[10px] text-slate-500">
                    After this assignment:{" "}
                    {Math.min(
                      selectedVehicle.capacity,
                      getVehicleOccupancy(selectedVehicle.id, reallocationId) +
                        (Number(pax) || 0),
                    )}{" "}
                    / {selectedVehicle.capacity}
                  </div>
                </div>
              )}
              {selectedRoute && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2">
                  <div className="text-sm font-semibold text-amber-800">
                    Calculated route schedule
                  </div>
                  <div className="mt-1 text-xs text-amber-700">
                    {selectedRoute.distance} km · {selectedRoute.duration}{" "}
                    driving hours · Ends{" "}
                    {startTime
                      ? addHoursToTime(startTime, selectedRoute.duration)
                      : "after start time"}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={clearAssignment}>
                Cancel
              </Button>
              <Button
                onClick={saveAssignment}
                className="bg-sky-600 text-white hover:bg-sky-700"
              >
                {reallocationId ? "Confirm Reallocation" : "Confirm Assignment"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={!!managementMode}
          onOpenChange={(open) => !open && setManagementMode(null)}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-4xl">
            {managementMode && (
              <>
                <DialogHeader>
                  <DialogTitle>
                    {managementConfig[managementMode].title}
                  </DialogTitle>
                  <DialogDescription>
                    {managementConfig[managementMode].description}
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3 py-2 sm:grid-cols-2">
                  {managementConfig[managementMode].fields.map((field) => (
                    <div key={field}>
                      <div className="mb-1 text-xs font-medium">
                        {field}{" "}
                        {field !== "Permit (Optional)" && (
                          <span className="text-red-500">*</span>
                        )}
                      </div>
                      {field === "Employee Driver" ? (
                        <Select
                          value={managementValues[field] || ""}
                          onValueChange={(value) =>
                            setManagementValues((current) => ({
                              ...current,
                              [field]: value,
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select employee driver" />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map((item) => (
                              <SelectItem key={item.id} value={item.name}>
                                {item.name} · {item.employeeNo}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : field === "Provider" ? (
                        <Select
                          value={managementValues[field] || ""}
                          onValueChange={(value) =>
                            setManagementValues((current) => ({
                              ...current,
                              [field]: value,
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select internal vehicles or vendor" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="internal">
                              FHIMS Internal Vehicles
                            </SelectItem>
                            {vendors
                              .filter((item) => item.status === "Active")
                              .map((item) => (
                                <SelectItem key={item.id} value={item.id}>
                                  {item.name} · {item.category}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          type={
                            field.toLowerCase().includes("hours") ||
                            field.toLowerCase().includes("count") ||
                            field.toLowerCase().includes("capacity") ||
                            field.toLowerCase().includes("distance")
                              ? "number"
                              : "text"
                          }
                          value={managementValues[field] || ""}
                          onChange={(event) =>
                            setManagementValues((current) => ({
                              ...current,
                              [field]: event.target.value,
                            }))
                          }
                          placeholder={
                            field === "Permit (Optional)"
                              ? "Enter permit reference if applicable"
                              : `Enter ${field.toLowerCase()}`
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setManagementMode(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveManagement}
                    className="bg-sky-600 text-white hover:bg-sky-700"
                  >
                    Save
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
